// Phase 24 — Admin User Intelligence detail drawer.
//
// Slide-in side-sheet with 6 tabs (Profile · Deals · Properties · AI Usage ·
// Viral · Activity). Triggered from AdminUsers.jsx; reads
// `GET /api/admin/users/{id}/detail` which the backend audit-logs as
// `admin_user_detail_viewed` so we have a provable access trail.

import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, MapPin, Smartphone, Link2, Calendar, DollarSign, TrendingUp, Users, Activity, Megaphone, AlertCircle, ShieldOff, KeyRound, UserCog, Trash2, Pencil, ShieldCheck, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import EditProfileTab from "./user_drawer/EditProfileTab";
import SecurityTab from "./user_drawer/SecurityTab";
import CommunicationTab from "./user_drawer/CommunicationTab";

function countryCodeToEmoji(code) {
  if (!code || code.length !== 2 || !/^[A-Za-z]+$/.test(code)) return "";
  const A = 0x1F1E6;
  return (
    String.fromCodePoint(A + code.toUpperCase().charCodeAt(0) - 65) +
    String.fromCodePoint(A + code.toUpperCase().charCodeAt(1) - 65)
  );
}

function shortUA(ua) {
  if (!ua) return "—";
  const m = ua.match(/(Chrome|Firefox|Safari|Edge|Opera|OPR)[/\s]([\d.]+)/);
  const browser = m ? `${m[1]} ${m[2].split(".")[0]}` : "browser";
  const os =
    /Windows/i.test(ua) ? "Windows" :
    /Mac OS X/i.test(ua) ? "macOS" :
    /Android/i.test(ua) ? "Android" :
    /iPhone|iPad/i.test(ua) ? "iOS" :
    /Linux/i.test(ua) ? "Linux" : "—";
  return `${browser} · ${os}`;
}

function Row({ label, value, mono, testid }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-slate-100 py-2 last:border-b-0" data-testid={testid}>
      <span className="flex-shrink-0 text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
      <span className={`text-right text-sm text-slate-800 ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</span>
    </div>
  );
}

function Stat({ label, value, hint, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-50 border-slate-200 text-slate-900",
    indigo: "bg-indigo-50 border-indigo-200 text-indigo-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
  };
  return (
    <div className={`rounded-xl border p-3 ${tones[tone]}`}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</div>
      <div className="mt-1 text-xl font-bold tabular-nums">{value}</div>
      {hint && <div className="mt-0.5 text-xs text-slate-500">{hint}</div>}
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  } catch {
    return iso;
  }
}

const TIMELINE_ICON = {
  signup: { Icon: UserCog, color: "bg-indigo-100 text-indigo-700" },
  login: { Icon: KeyRound, color: "bg-blue-100 text-blue-700" },
  deal_created: { Icon: TrendingUp, color: "bg-emerald-100 text-emerald-700" },
  verdict_pdf_shared: { Icon: Link2, color: "bg-purple-100 text-purple-700" },
  signup_attributed_to_share: { Icon: Megaphone, color: "bg-amber-100 text-amber-700" },
  referral_signup_completed: { Icon: Megaphone, color: "bg-emerald-100 text-emerald-700" },
  paywall_hit: { Icon: AlertCircle, color: "bg-red-100 text-red-700" },
};

export default function UserDetailDrawer({ user, open, onClose, onImpersonate, onResetPw, onSuspend, onDelete }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !user?.id) return;
    setLoading(true);
    setData(null);
    api.get(`/admin/users/${user.id}/detail`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, user?.id]);

  if (!user) return null;

  const profile = data?.user || {};
  const flag = countryCodeToEmoji(profile.signup_country_code || profile.last_login_country_code || user.signup_country_code);
  const isPro = ["pro", "premium", "enterprise"].includes((profile.subscription_plan || user.subscription_plan || "").toLowerCase());

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full max-w-2xl overflow-y-auto sm:max-w-2xl" data-testid="user-detail-drawer">
        <SheetHeader className="border-b border-slate-200 pb-4">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-600 text-base font-bold text-white">
              {(profile.name || user.name || profile.email || "?")[0]?.toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <SheetTitle className="flex items-center gap-2 truncate text-base">
                {profile.name || user.name} {flag && <span data-testid="user-flag">{flag}</span>}
              </SheetTitle>
              <p className="truncate text-xs text-slate-500">{profile.email || user.email}</p>
            </div>
            <div className="flex flex-shrink-0 items-center gap-1">
              <span
                className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                  isPro
                    ? "bg-emerald-100 text-emerald-800"
                    : "bg-slate-100 text-slate-600"
                }`}
                data-testid="drawer-plan-pill"
              >
                {isPro ? "✨ Pro" : "Free"}
              </span>
            </div>
          </div>

          {/* Action buttons */}
          <div className="mt-3 flex flex-wrap gap-1.5">
            <Button size="sm" variant="outline" onClick={() => onImpersonate?.(user)} data-testid="drawer-impersonate">
              <UserCog className="me-1 h-3.5 w-3.5" /> Impersonate
            </Button>
            <Button size="sm" variant="outline" onClick={() => onResetPw?.(user)} data-testid="drawer-reset-pw">
              <KeyRound className="me-1 h-3.5 w-3.5" /> Reset PW
            </Button>
            <Button size="sm" variant="outline" onClick={() => onSuspend?.(user)} data-testid="drawer-suspend">
              <ShieldOff className="me-1 h-3.5 w-3.5" /> {user.is_suspended ? "Unsuspend" : "Suspend"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => onDelete?.(user)} className="ms-auto text-red-600 hover:bg-red-50" data-testid="drawer-delete">
              <Trash2 className="me-1 h-3.5 w-3.5" /> Delete
            </Button>
          </div>
        </SheetHeader>

        {loading && (
          <div className="flex items-center gap-2 pt-6 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading user intelligence…
          </div>
        )}

        {data && !loading && (
          <Tabs defaultValue="profile" className="mt-4" data-testid="drawer-tabs">
            <TabsList className="grid w-full grid-cols-9 text-[10px]">
              <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
              <TabsTrigger value="edit" data-testid="tab-edit">
                <Pencil className="me-0.5 h-3 w-3" /> Edit
              </TabsTrigger>
              <TabsTrigger value="security" data-testid="tab-security">
                <ShieldCheck className="me-0.5 h-3 w-3" /> Security
              </TabsTrigger>
              <TabsTrigger value="comm" data-testid="tab-comm">
                <MessageSquare className="me-0.5 h-3 w-3" /> Comm
              </TabsTrigger>
              <TabsTrigger value="deals" data-testid="tab-deals">Deals</TabsTrigger>
              <TabsTrigger value="properties" data-testid="tab-properties">Props</TabsTrigger>
              <TabsTrigger value="ai" data-testid="tab-ai">AI</TabsTrigger>
              <TabsTrigger value="viral" data-testid="tab-viral">Viral</TabsTrigger>
              <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
            </TabsList>

            {/* Profile */}
            <TabsContent value="profile" className="pt-4">
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <MapPin className="h-3 w-3" /> Origin
                </div>
                <Row label="Signup IP" value={profile.signup_ip} mono testid="profile-signup-ip" />
                <Row
                  label="Signup country"
                  value={profile.signup_country ? `${countryCodeToEmoji(profile.signup_country_code)} ${profile.signup_country}` : "(not yet enriched)"}
                />
                <Row label="Signup city" value={profile.signup_city} />
                <Row label="Signup device" value={shortUA(profile.signup_user_agent)} testid="profile-signup-ua" />
                <Row label="Accept-Language" value={profile.signup_accept_language} mono />
                <Row label="Referer" value={profile.signup_referer} mono />
                {profile.signup_utm && Object.keys(profile.signup_utm).length > 0 && (
                  <Row label="UTM" value={Object.entries(profile.signup_utm).map(([k, v]) => `${k}=${v}`).join(" · ")} mono />
                )}
              </div>

              <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
                <div className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <Activity className="h-3 w-3" /> Session
                </div>
                <Row label="Logins" value={profile.login_count} />
                <Row label="Last login" value={profile.last_login_at ? `${timeAgo(profile.last_login_at)} · ${profile.last_login_ip || "—"}` : "—"} />
                <Row label="Last country" value={profile.last_login_country ? `${countryCodeToEmoji(profile.last_login_country_code)} ${profile.last_login_country}` : "—"} />
                <Row label="Member since" value={profile.created_at ? new Date(profile.created_at).toLocaleDateString() : "—"} />
                <Row label="Pro expires" value={profile.pro_expires_at ? new Date(profile.pro_expires_at).toLocaleDateString() : "—"} />
              </div>
            </TabsContent>

            {/* Phase 28 — Edit profile (dynamic, schema-driven) */}
            <TabsContent value="edit" className="pt-4">
              <EditProfileTab
                userId={user.id}
                onPatched={() => {
                  // Refresh the drawer-wide detail snapshot so the header email/name
                  // tracks the patched values immediately.
                  api.get(`/admin/users/${user.id}/detail`).then(({ data }) => setData(data)).catch(() => {});
                }}
              />
            </TabsContent>

            {/* Phase 28 — Security */}
            <TabsContent value="security" className="pt-4">
              <SecurityTab
                user={profile}
                userId={user.id}
                onChanged={() => {
                  api.get(`/admin/users/${user.id}/detail`).then(({ data }) => setData(data)).catch(() => {});
                }}
              />
            </TabsContent>

            {/* Phase 28 — Communication */}
            <TabsContent value="comm" className="pt-4">
              <CommunicationTab user={profile} userId={user.id} />
            </TabsContent>

            {/* Deals */}
            <TabsContent value="deals" className="pt-4">
              <div className="grid grid-cols-2 gap-3">
                <Stat label="Total deals" value={data.deals.total} tone="indigo" />
                <Stat label="Owned" value={data.deals.owned_count} tone="emerald" hint={data.deals.total_market_value ? `~$${(data.deals.total_market_value / 1000).toFixed(0)}k value` : null} />
              </div>
              <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">By type</div>
                <div className="mt-2 space-y-1.5" data-testid="drawer-deals-by-type">
                  {Object.entries(data.deals.by_type).map(([k, v]) => {
                    const total = Object.values(data.deals.by_type).reduce((s, n) => s + n, 0) || 1;
                    const pct = Math.round((v / total) * 100);
                    return (
                      <div key={k}>
                        <div className="flex items-center justify-between text-xs">
                          <span className="capitalize">{k}</span>
                          <span className="tabular-nums">{v} · {pct}%</span>
                        </div>
                        <div className="mt-0.5 h-1 overflow-hidden rounded-full bg-slate-100">
                          <div className="h-full bg-indigo-500" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {data.deals.recent_5.length > 0 && (
                <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Recent 5</div>
                  <ul className="mt-2 divide-y divide-slate-100">
                    {data.deals.recent_5.map((d) => (
                      <li key={d.id} className="flex items-center justify-between py-2 text-sm">
                        <span className="min-w-0 flex-1 truncate text-slate-700">{d.address}</span>
                        <span className="ms-3 text-xs capitalize text-slate-500">{d.deal_type}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </TabsContent>

            {/* Properties */}
            <TabsContent value="properties" className="pt-4">
              <Stat label="Total properties" value={data.properties.total} tone="indigo" />
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">By type</div>
                  <div className="mt-2 space-y-1.5">
                    {Object.entries(data.properties.by_type).length === 0
                      ? <div className="text-xs text-slate-400">No properties yet</div>
                      : Object.entries(data.properties.by_type).map(([k, v]) => (
                          <div key={k} className="flex items-center justify-between text-sm">
                            <span className="capitalize text-slate-700">{(k || "—").replace(/_/g, " ")}</span>
                            <span className="tabular-nums text-slate-500">{v}</span>
                          </div>
                        ))}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Top states</div>
                  <div className="mt-2 space-y-1.5">
                    {Object.entries(data.properties.by_state).length === 0
                      ? <div className="text-xs text-slate-400">—</div>
                      : Object.entries(data.properties.by_state).map(([k, v]) => (
                          <div key={k}>
                            <div className="flex items-center justify-between text-xs">
                              <span className="font-semibold">{k}</span>
                              <span className="tabular-nums text-slate-500">{v}</span>
                            </div>
                            <div className="mt-0.5 h-1 overflow-hidden rounded-full bg-slate-100">
                              <div className="h-full bg-indigo-500" style={{ width: `${Math.min(100, (v / Math.max(...Object.values(data.properties.by_state))) * 100)}%` }} />
                            </div>
                          </div>
                        ))}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* AI */}
            <TabsContent value="ai" className="pt-4">
              <div className="grid grid-cols-2 gap-3">
                <Stat label="Calls (30d)" value={data.ai_usage.calls_30d.toLocaleString()} tone="indigo" />
                <Stat label="Cost (30d)" value={`$${data.ai_usage.cost_30d_usd.toFixed(2)}`} tone="emerald" />
                <Stat label="Tokens (30d)" value={(data.ai_usage.tokens_30d || 0).toLocaleString()} />
                <Stat label="Lifetime cost" value={`$${data.ai_usage.cost_lifetime_usd.toFixed(2)}`} tone="amber" />
              </div>
              <p className="mt-4 text-xs text-slate-500">
                Detail line charts will land in a future iteration — for now the daily breakdown lives in the AI Usage tab of the main admin.
              </p>
            </TabsContent>

            {/* Viral */}
            <TabsContent value="viral" className="pt-4">
              <div className="grid grid-cols-2 gap-3">
                <Stat label="Referrals made" value={data.viral.referrals_made} tone="emerald" />
                <Stat label="Pro months earned" value={data.viral.pro_months_earned_from_referrals} tone="emerald" />
                <Stat label="PDFs shared" value={data.viral.pdfs_shared} tone="indigo" />
                <Stat label="Paywall hits" value={data.viral.paywall_hits} tone="amber" />
              </div>
              {data.viral.attributed_signup_referrer?.email && (
                <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm">
                  <span className="font-semibold text-emerald-900">Inbound: </span>
                  <span className="text-emerald-800">Was referred by {data.viral.attributed_signup_referrer.email}</span>
                </div>
              )}
            </TabsContent>

            {/* Activity */}
            <TabsContent value="activity" className="pt-4">
              <ul className="space-y-2" data-testid="drawer-timeline">
                {data.activity_timeline.length === 0 && (
                  <li className="text-sm text-slate-500">No activity yet.</li>
                )}
                {data.activity_timeline.map((evt, i) => {
                  const def = TIMELINE_ICON[evt.type] || { Icon: Activity, color: "bg-slate-100 text-slate-600" };
                  const Icon = def.Icon;
                  return (
                    <li
                      key={`${evt.type}-${evt.timestamp || ""}-${i}`}
                      className="flex items-start gap-2.5 rounded-lg border border-slate-100 bg-white px-3 py-2"
                    >
                      <div className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full ${def.color}`}>
                        <Icon className="h-3.5 w-3.5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-slate-800">{evt.summary || evt.type}</p>
                        <p className="text-xs text-slate-400">{timeAgo(evt.timestamp)}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </TabsContent>
          </Tabs>
        )}
      </SheetContent>
    </Sheet>
  );
}

export { countryCodeToEmoji };
